import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const SignUpPage = ({ onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    photo: '',
    goal: 1400,
    bio: '',
    fullBio: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'goal' ? parseInt(value) || 1400 : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!formData.name.trim() || !formData.bio.trim()) {
      alert('Please fill in all required fields');
      setIsSubmitting(false);
      return;
    }

    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      onSubmit({
        ...formData,
        fullBio: formData.fullBio || formData.bio
      });
      
      setSubmitted(true);
    } catch (error) {
      alert('Error submitting application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto text-center">
          <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 md:p-8">
            <div className="text-5xl sm:text-6xl mb-3 sm:mb-4">✅</div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-3 sm:mb-4">Application Submitted!</h2>
            <p className="text-base sm:text-lg text-gray-600 mb-4 sm:mb-6">
              Thank you for your interest in joining our Costa Rica missions team.
              Your application has been submitted and is pending admin approval.
            </p>
            <p className="text-sm sm:text-base text-gray-500 mb-4 sm:mb-6">
              You'll be notified once your application has been reviewed.
              In the meantime, please pray about this opportunity and prepare your heart for what God has in store.
            </p>
            <Link
              to="/"
              className="bg-blue-600 text-white px-4 py-2 sm:px-6 sm:py-3 rounded-lg hover:bg-blue-700 transition-colors inline-block text-sm sm:text-base"
            >
              Return to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <Link to="/" className="text-blue-600 hover:text-blue-800 mb-4 sm:mb-6 inline-block">
        ← Back to home
      </Link>

      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 md:p-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-1 sm:mb-2">Join Our Mission Team</h1>
          <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8">
            Apply to be part of our Costa Rica missions trip. Fill out the form below to get started on your fundraising journey.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Full Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                placeholder="Enter your full name"
              />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Profile Photo URL
              </label>
              <input
                type="url"
                name="photo"
                value={formData.photo}
                onChange={handleChange}
                className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                placeholder="https://example.com/your-photo.jpg"
              />
              <p className="text-xs sm:text-sm text-gray-500 mt-1">
                Optional: Provide a URL to your profile photo. If left blank, a default image will be used.
              </p>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Fundraising Goal
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm sm:text-base">$</span>
                <input
                  type="number"
                  name="goal"
                  value={formData.goal}
                  onChange={handleChange}
                  min="1000"
                  max="5000"
                  className="w-full pl-8 pr-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                />
              </div>
              <p className="text-xs sm:text-sm text-gray-500 mt-1">
                The recommended goal is $1,400 to cover trip costs. You can adjust this amount if needed.
              </p>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Short Bio/Story *
              </label>
              <textarea
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                required
                rows={3}
                className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                placeholder="Write a brief description about yourself and why you want to join the missions trip..."
              />
              <p className="text-xs sm:text-sm text-gray-500 mt-1">
                This will be displayed on the main page (keep it concise, 1-2 sentences).
              </p>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Detailed Story (Optional)
              </label>
              <textarea
                name="fullBio"
                value={formData.fullBio}
                onChange={handleChange}
                rows={5}
                className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                placeholder="Share more about your heart for missions, what you hope to accomplish, and why donors should support you..."
              />
              <p className="text-xs sm:text-sm text-gray-500 mt-1">
                This detailed story will be displayed on your individual fundraising page. If left blank, your short bio will be used.
              </p>
            </div>

            <div className="bg-blue-50 p-3 sm:p-4 rounded-lg">
              <h3 className="font-semibold text-blue-800 mb-2 text-sm sm:text-base">What happens next?</h3>
              <ul className="text-xs sm:text-sm text-blue-700 space-y-1">
                <li>• Your application will be reviewed by our missions team</li>
                <li>• Once approved, your fundraising page will go live</li>
                <li>• You'll be able to share your page with friends and family</li>
                <li>• All donations will be processed securely through Stripe</li>
              </ul>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-blue-600 text-white py-3 px-4 sm:px-6 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm sm:text-base"
            >
              {isSubmitting ? 'Submitting Application...' : 'Submit Application'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SignUpPage;